<template>
  <div class="iq-thinking-container">
    <!-- Thinking Header - Clickable to expand/collapse -->
    <div class="iq-thinking-header" :class="{ 'is-expanded': isExpanded, 'is-thinking': isThinking }"
      @click="toggleExpand">
      <div class="iq-thinking-indicator">
        <!-- Animated sparkle/thinking icon -->
        <div class="thinking-icon" :class="{ 'is-animating': isThinking }">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L13.09 8.26L19 9L13.09 10.74L12 17L10.91 10.74L5 9L10.91 8.26L12 2Z" fill="currentColor"
              class="sparkle-main" />
            <path d="M19 15L19.54 17.54L22 18L19.54 18.46L19 21L18.46 18.46L16 18L18.46 17.54L19 15Z"
              fill="currentColor" class="sparkle-small-1" />
            <path d="M5 15L5.54 17.54L8 18L5.54 18.46L5 21L4.46 18.46L2 18L4.46 17.54L5 15Z" fill="currentColor"
              class="sparkle-small-2" />
          </svg>
        </div>

        <!-- Thinking text with animation -->
        <span class="thinking-text">
          {{ displayText }}
        </span>

        <!-- Elapsed time -->
        <span v-if="elapsedTime > 0" class="thinking-elapsed">
          {{ formattedElapsedTime }}
        </span>
      </div>

      <!-- Expand/Collapse chevron -->
      <div class="iq-thinking-chevron" :class="{ 'is-expanded': isExpanded }">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </div>
    </div>

    <!-- Expandable Reasoning Content -->
    <Transition name="reasoning-expand">
      <div v-if="isExpanded" class="iq-thinking-content">
        <!-- Loading State - Animated lines while waiting -->
        <div v-if="isThinking && !reasoning" class="thinking-loading-state mt-2">
          <div class="thinking-loading-lines">
            <div class="thinking-line thinking-line-1"></div>
            <div class="thinking-line thinking-line-2"></div>
            <div class="thinking-line thinking-line-3"></div>
          </div>
          <p class="thinking-loading-text">{{ loadingStepText }}</p>
        </div>

        <!-- Reasoning Content - Shows when available with word-by-word streaming -->
        <div v-else-if="reasoning" ref="reasoningContainer" class="reasoning-text-container mt-2">
          <div class="reasoning-text">
            <span v-html="formattedStreamedText"></span>
            <span v-if="isStreaming" class="streaming-cursor">|</span>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from "vue";

const props = defineProps({
  /**
   * The reasoning/thinking text from the AI
   */
  reasoning: {
    type: String,
    default: "",
  },
  /**
   * Whether the AI is currently thinking/processing
   */
  isThinking: {
    type: Boolean,
    default: false,
  },
  /**
   * Auto-expand when reasoning is available
   */
  autoExpand: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["toggle"]);

// State
const isExpanded = ref(false);
const thinkingTextIndex = ref(0);
const loadingStepIndex = ref(0);
const startTime = ref(null);
const elapsedTime = ref(0);
const streamedText = ref("");
const isStreaming = ref(false);
const streamingComplete = ref(false); // Flag to prevent re-streaming once complete
const lastStreamedReasoning = ref(""); // Track what reasoning was streamed
const userManuallyExpanded = ref(false);
const wasAutoClosed = ref(false);
const reasoningContainer = ref(null);
let thinkingInterval = null;
let elapsedInterval = null;
let loadingStepInterval = null;
let streamingInterval = null;

// Thinking text variations for animation
const thinkingTexts = ["Thinking", "Thinking.", "Thinking..", "Thinking..."];

// Loading step messages (Gemini-style)
const loadingSteps = [
  "Analyzing your question...",
  "Gathering relevant context...",
  "Processing information...",
  "Formulating response...",
  "Reviewing analysis...",
  "Almost there...",
];

// Animated thinking text
const thinkingTextAnimated = computed(() => {
  return thinkingTexts[thinkingTextIndex.value];
});

// Display text for header
const displayText = computed(() => {
  if (props.isThinking) {
    return thinkingTextAnimated.value;
  }
  return "Thought process";
});

// Current loading step text
const loadingStepText = computed(() => {
  return loadingSteps[loadingStepIndex.value];
});

// Format elapsed time
const formattedElapsedTime = computed(() => {
  if (elapsedTime.value < 1000) return "";
  const seconds = Math.floor(elapsedTime.value / 1000);
  return `${seconds}s`;
});

// Convert markdown to HTML for proper formatting
const formatMarkdownToHtml = (text) => {
  if (!text) return "";
  // Convert markdown bold (**text** or __text__) to HTML bold - use [^*]+ to match any chars except *
  text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  // Convert markdown italic (*text* or _text_) to HTML italic
  text = text.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  text = text.replace(/_([^_]+)_/g, "<em>$1</em>");
  // Convert markdown headers (# ## ###) to styled text
  text = text.replace(/^#{1,6}\s+(.+)$/gm, "<strong class='reasoning-heading'>$1</strong>");
  // Convert line breaks
  text = text.replace(/\n/g, "<br>");
  return text;
};

// Auto-scroll to bottom of reasoning container
const scrollToBottom = () => {
  if (reasoningContainer.value) {
    reasoningContainer.value.scrollTop = reasoningContainer.value.scrollHeight;
  }
};

// Computed formatted streamed text
const formattedStreamedText = computed(() => {
  return formatMarkdownToHtml(streamedText.value);
});

// Word-by-word streaming logic
const streamText = () => {
  if (!props.reasoning) {
    streamedText.value = "";
    isStreaming.value = false;
    return;
  }

  // Use raw reasoning text (with markdown) for streaming
  const targetReasoning = props.reasoning;

  // Clear any existing streaming interval
  if (streamingInterval) {
    clearInterval(streamingInterval);
  }

  // If streaming was already completed for this exact reasoning, don't restart
  // This prevents re-typing when dropdown is reopened
  if (streamingComplete.value && lastStreamedReasoning.value === targetReasoning) {
    isStreaming.value = false;
    // Make sure streamedText has the full content
    if (streamedText.value !== targetReasoning) {
      streamedText.value = targetReasoning;
    }
    return;
  }

  // If streaming is already complete (text matches), don't restart
  if (streamedText.value === targetReasoning) {
    isStreaming.value = false;
    streamingComplete.value = true;
    lastStreamedReasoning.value = targetReasoning;
    return;
  }

  // If we haven't started streaming or text changed completely, reset
  if (!streamedText.value || !targetReasoning.startsWith(streamedText.value)) {
    streamedText.value = "";
    streamingComplete.value = false;
  }

  isStreaming.value = true;

  // Stream words one by one
  streamingInterval = setInterval(() => {
    const currentLength = streamedText.value.length;
    const targetLength = targetReasoning.length;

    if (currentLength < targetLength) {
      // Get remaining text to stream
      const remainingText = targetReasoning.substring(currentLength);
      // Get next word (including spaces)
      const nextWordMatch = remainingText.match(/^\S+\s*/);

      if (nextWordMatch) {
        streamedText.value += nextWordMatch[0];
        // Auto-scroll to show latest content
        nextTick(() => scrollToBottom());
      } else {
        // No more words, but might have trailing spaces or special chars
        streamedText.value = targetReasoning;
        isStreaming.value = false;
        streamingComplete.value = true;
        lastStreamedReasoning.value = targetReasoning;
        clearInterval(streamingInterval);
        streamingInterval = null;
        // Auto-close when streaming completes
        setTimeout(() => {
          if (!props.isThinking && !userManuallyExpanded.value) {
            isExpanded.value = false;
            wasAutoClosed.value = true;
          }
        }, 500); // Small delay before auto-closing
      }
    } else {
      // Streaming complete
      isStreaming.value = false;
      streamingComplete.value = true;
      lastStreamedReasoning.value = targetReasoning;
      clearInterval(streamingInterval);
      streamingInterval = null;
      // Auto-close when streaming completes
      setTimeout(() => {
        if (!props.isThinking && !userManuallyExpanded.value) {
          isExpanded.value = false;
          wasAutoClosed.value = true;
        }
      }, 500); // Small delay before auto-closing
    }
  }, 30); // Adjust speed here (30ms per word = ~33 words/second)
};

// Stop streaming
const stopStreaming = () => {
  if (streamingInterval) {
    clearInterval(streamingInterval);
    streamingInterval = null;
  }
  isStreaming.value = false;
};

// Toggle expand/collapse
const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
  // Track if user manually interacted
  if (isExpanded.value) {
    // User manually expanded - reset auto-closed flag
    userManuallyExpanded.value = true;
    wasAutoClosed.value = false;
  } else {
    // If user manually collapsed, mark it so it won't auto-expand again
    userManuallyExpanded.value = true;
  }
  emit("toggle", isExpanded.value);
};

// Start thinking animation
const startThinkingAnimation = () => {
  startTime.value = Date.now();
  thinkingTextIndex.value = 0;
  loadingStepIndex.value = 0;
  elapsedTime.value = 0;

  // Animate thinking text (dots)
  thinkingInterval = setInterval(() => {
    thinkingTextIndex.value =
      (thinkingTextIndex.value + 1) % thinkingTexts.length;
  }, 400);

  // Track elapsed time
  elapsedInterval = setInterval(() => {
    elapsedTime.value = Date.now() - startTime.value;
  }, 100);

  // Cycle through loading steps
  loadingStepInterval = setInterval(() => {
    loadingStepIndex.value = (loadingStepIndex.value + 1) % loadingSteps.length;
  }, 3000);
};

// Stop thinking animation
const stopThinkingAnimation = () => {
  if (thinkingInterval) {
    clearInterval(thinkingInterval);
    thinkingInterval = null;
  }
  if (elapsedInterval) {
    clearInterval(elapsedInterval);
    elapsedInterval = null;
  }
  if (loadingStepInterval) {
    clearInterval(loadingStepInterval);
    loadingStepInterval = null;
  }
};

// Watch for thinking state changes
watch(
  () => props.isThinking,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      startThinkingAnimation();
      // Reset manual expansion flag when new thinking starts
      userManuallyExpanded.value = false;
      // Auto-expand when starting to think (only if not previously auto-closed)
      if (!wasAutoClosed.value) {
        isExpanded.value = true;
      }
    } else if (!newVal && oldVal) {
      stopThinkingAnimation();
    }
  },
  { immediate: true }
);

// Watch reasoning changes and stream word by word
watch(
  () => props.reasoning,
  (newVal, oldVal) => {
    // Reset flags when reasoning is cleared (new session)
    if (!newVal) {
      userManuallyExpanded.value = false;
      wasAutoClosed.value = false;
      streamingComplete.value = false;
      lastStreamedReasoning.value = "";
    } else if (oldVal && !newVal.startsWith(oldVal)) {
      // Reasoning changed completely (new content)
      userManuallyExpanded.value = false;
      wasAutoClosed.value = false;
      streamingComplete.value = false;
      lastStreamedReasoning.value = "";
    }

    // Start or continue streaming when reasoning changes
    // But skip if already streamed this exact content
    if (newVal) {
      // Only call streamText if this is new content or not yet complete
      if (!streamingComplete.value || lastStreamedReasoning.value !== newVal) {
        streamText();
      }
    } else {
      // Clear streaming when reasoning is cleared
      stopStreaming();
      streamedText.value = "";
      streamingComplete.value = false;
      lastStreamedReasoning.value = "";
    }
  },
  { immediate: true }
);

// Cleanup
onUnmounted(() => {
  stopThinkingAnimation();
  stopStreaming();
});
</script>

<style scoped>
.iq-thinking-container {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg,
      rgba(139, 92, 246, 0.08) 0%,
      rgba(59, 130, 246, 0.08) 100%);
  border: 1px solid rgba(139, 92, 246, 0.2);
  margin-bottom: 12px;
}

.iq-thinking-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.iq-thinking-header:hover {
  background: rgba(139, 92, 246, 0.1);
}

.iq-thinking-header.is-thinking {
  background: linear-gradient(90deg,
      rgba(139, 92, 246, 0.15) 0%,
      rgba(59, 130, 246, 0.15) 50%,
      rgba(139, 92, 246, 0.15) 100%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite linear;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.iq-thinking-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
}

.thinking-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8b5cf6;
  transition: transform 0.3s ease;
}

.thinking-icon.is-animating {
  animation: sparkle-pulse 1.5s infinite ease-in-out;
}

.thinking-icon.is-animating .sparkle-main {
  animation: sparkle-rotate 2s infinite linear;
  transform-origin: center;
}

.thinking-icon.is-animating .sparkle-small-1 {
  animation: sparkle-blink 1s infinite ease-in-out 0.3s;
}

.thinking-icon.is-animating .sparkle-small-2 {
  animation: sparkle-blink 1s infinite ease-in-out 0.6s;
}

@keyframes sparkle-pulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.15);
  }
}

@keyframes sparkle-rotate {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes sparkle-blink {

  0%,
  100% {
    opacity: 0.4;
  }

  50% {
    opacity: 1;
  }
}

.thinking-text {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #a78bfa;
  min-width: 80px;
}

.thinking-elapsed {
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #6b7280;
  margin-left: 4px;
}

.iq-thinking-chevron {
  color: #a78bfa;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.iq-thinking-chevron.is-expanded {
  transform: rotate(180deg);
}

/* Expandable Content */
.iq-thinking-content {
  padding: 0 16px 16px 16px;
  overflow: hidden;
  overflow-x: hidden;
  max-width: 100%;
}

/* Loading State - Animated lines while waiting for reasoning */
.thinking-loading-state {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.thinking-loading-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.thinking-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg,
      rgba(139, 92, 246, 0.2) 0%,
      rgba(139, 92, 246, 0.4) 50%,
      rgba(139, 92, 246, 0.2) 100%);
  background-size: 200% 100%;
  animation: line-shimmer 1.5s infinite ease-in-out;
}

.thinking-line-1 {
  width: 90%;
  animation-delay: 0s;
}

.thinking-line-2 {
  width: 75%;
  animation-delay: 0.2s;
}

.thinking-line-3 {
  width: 60%;
  animation-delay: 0.4s;
}

@keyframes line-shimmer {
  0% {
    background-position: 200% 0;
    opacity: 0.6;
  }

  50% {
    opacity: 1;
  }

  100% {
    background-position: -200% 0;
    opacity: 0.6;
  }
}

.thinking-loading-text {
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #a78bfa;
  margin: 0;
  animation: fade-in-out 3s infinite ease-in-out;
}

@keyframes fade-in-out {

  0%,
  100% {
    opacity: 0.5;
  }

  50% {
    opacity: 1;
  }
}

.reasoning-text-container {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
}

.reasoning-text-container::-webkit-scrollbar {
  width: 6px;
}

.reasoning-text-container::-webkit-scrollbar-track {
  background: rgba(139, 92, 246, 0.1);
  border-radius: 3px;
}

.reasoning-text-container::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.3);
  border-radius: 3px;
}

.reasoning-text-container::-webkit-scrollbar-thumb:hover {
  background: rgba(139, 92, 246, 0.5);
}

.reasoning-text {
  font-family: "Inter", sans-serif;
  font-size: 13px;
  line-height: 1.7;
  color: #c4b5fd;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
}

.reasoning-text :deep(strong) {
  color: #e9d5ff;
  font-weight: 600;
}

.reasoning-text :deep(em) {
  font-style: italic;
  color: #ddd6fe;
}

.reasoning-text :deep(.reasoning-heading) {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #e9d5ff;
  margin: 12px 0 6px 0;
}

.streaming-cursor {
  display: inline-block;
  width: 2px;
  height: 16px;
  background-color: #8b5cf6;
  margin-left: 2px;
  animation: blink 1s infinite;
  vertical-align: middle;
}

@keyframes blink {

  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0;
  }
}


/* Transition animations */
.reasoning-expand-enter-active {
  animation: expand-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.reasoning-expand-leave-active {
  animation: expand-out 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes expand-in {
  0% {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }

  100% {
    opacity: 1;
    max-height: 500px;
    transform: translateY(0);
  }
}

@keyframes expand-out {
  0% {
    opacity: 1;
    max-height: 500px;
    transform: translateY(0);
  }

  100% {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
}
</style>
